// web/src/screens/admin/games_list.ts — Plan 11-06 Task 2.
//
// Lists all games (draft + published) with a status pill per row, and
// surfaces a "+ New Game" button that prompts for a short_code, POSTs to
// /api/admin/games, and navigates to the new game's edit screen.
//
// All user-supplied / API-supplied text rendered via textContent only
// (T-11-09a). NO raw-HTML setters anywhere in this file.

import { renderAdminShell, showAdminToast } from "./admin_shell.js";
import { adminGet, adminPost, AdminError } from "../../api/admin.js";
import type { AdminGameDTO } from "../../types/api.js";

function navigateAdmin(path: string): void {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function mount(root: HTMLElement, _props?: unknown): () => void {
  const main = renderAdminShell(root, [{ label: "Games" }]);

  const toolbar = document.createElement("div");
  toolbar.setAttribute("style", "display: flex; gap: 12px; align-items: center; margin-bottom: 16px;");
  const newBtn = document.createElement("button");
  newBtn.type = "button";
  newBtn.textContent = "+ New Game";
  newBtn.setAttribute("style", "padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  toolbar.appendChild(newBtn);
  main.appendChild(toolbar);

  const status = document.createElement("p");
  status.textContent = "Loading…";
  status.setAttribute("style", "color: #6b7280; font-size: 14px;");
  main.appendChild(status);

  const listEl = document.createElement("ul");
  listEl.setAttribute("style", "list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px;");
  main.appendChild(listEl);

  function renderRow(game: AdminGameDTO): HTMLLIElement {
    const li = document.createElement("li");
    li.setAttribute("style", "display: flex; align-items: center; gap: 12px; padding: 10px 14px; background: white; border: 1px solid #e5e7eb; border-radius: 6px;");

    // Picker display ordinal (game.sequence). Unsequenced games show "—" and
    // sort after every sequenced game (server: ORDER BY sequence NULLS LAST).
    const seq = document.createElement("span");
    seq.textContent = game.sequence != null ? `#${game.sequence}` : "—";
    seq.setAttribute("style", "min-width: 32px; text-align: right; color: #6b7280; font-size: 13px; font-variant-numeric: tabular-nums;");
    li.appendChild(seq);

    const a = document.createElement("a");
    a.textContent = game.short_code ?? "(no short_code)"; // textContent
    a.setAttribute("href", `/admin/games/${encodeURIComponent(game.short_code ?? "")}`);
    a.setAttribute("style", "color: #1d4ed8; text-decoration: none; font-weight: 500; flex: 1;");
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const sc = game.short_code;
      if (!sc) return;
      navigateAdmin(`/admin/games/${encodeURIComponent(sc)}`);
    });
    li.appendChild(a);

    const pill = document.createElement("span");
    const isPublished = game.published_at != null && game.published_at !== "";
    pill.textContent = isPublished ? "published" : "draft";
    pill.setAttribute(
      "style",
      isPublished
        ? "padding: 2px 8px; border-radius: 999px; background: #dcfce7; color: #166534; font-size: 12px;"
        : "padding: 2px 8px; border-radius: 999px; background: #f3f4f6; color: #374151; font-size: 12px;",
    );
    li.appendChild(pill);

    return li;
  }

  async function load(): Promise<void> {
    try {
      const games = await adminGet<AdminGameDTO[]>("/api/admin/games");
      listEl.replaceChildren();
      if (games.length === 0) {
        status.textContent = "No games yet — create one with the “+ New Game” button.";
        return;
      }
      status.textContent = `${games.length} game${games.length === 1 ? "" : "s"}.`;
      for (const g of games) listEl.appendChild(renderRow(g));
    } catch (err) {
      if (err instanceof AdminError && (err.status === 401 || err.status === 403)) {
        status.textContent = "Not signed in — visit /login to authenticate, then refresh.";
      } else {
        status.textContent = `Failed to load games: ${(err as Error).message}`;
      }
    }
  }

  async function onNewGame(): Promise<void> {
    // MVP-1 inline prompt. The author-experience improvement (modal w/
    // validation) is deferred — short_code uniqueness is enforced server-
    // side via 23505 → 409 duplicate_short_code (Plan 11-04a).
    const sc = window.prompt("New game short_code (lowercase, [a-z0-9_-]+):");
    if (!sc) return;
    try {
      // v3.2 required-on-create fields: short_code, title, theme_color.
      const created = await adminPost<AdminGameDTO, AdminGameDTO>("/api/admin/games", {
        short_code: sc,
        title: sc,
        theme_color: "#5E3FBE",
      });
      showAdminToast(document.body, `Created ${created.short_code}`);
      navigateAdmin(`/admin/games/${encodeURIComponent(created.short_code ?? sc)}`);
    } catch (err) {
      if (err instanceof AdminError && err.status === 409) {
        showAdminToast(document.body, "short_code already exists");
      } else if (err instanceof AdminError && err.status === 422) {
        showAdminToast(document.body, "Invalid input — short_code must match [a-z0-9_-]+");
      } else {
        showAdminToast(document.body, `Create failed: ${(err as Error).message}`);
      }
    }
  }

  newBtn.addEventListener("click", onNewGame);

  void load();

  return () => {
    newBtn.removeEventListener("click", onNewGame);
  };
}
